/**
 * Azure Static Web Apps /.auth/me returns clientPrincipal; userDetails is sometimes masked (e.g. bau*****)
 * for privacy. Real email and name are usually in the claims array — use these helpers before calling /api/users/sync.
 */

export type AuthClaim = { typ?: string; val?: string };

export type ClientPrincipalWithClaims = {
  userId: string;
  userDetails: string;
  identityProvider?: string;
  userRoles: string[];
  claims?: AuthClaim[];
};

/** Heuristic: SWA / Entra sometimes masks the local part of the identifier. */
function looksMasked(str: string): boolean {
  const s = str.trim();
  if (!s) return true;
  if (s.includes("*****")) return true;
  if (s.includes("*") && s.includes("@")) {
    const local = s.split("@")[0] ?? "";
    if (local.includes("*")) return true;
  }
  return false;
}

function looksLikeEmail(str: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str.trim());
}

function claimTypMatches(typ: string, candidates: string[]): boolean {
  const t = typ.toLowerCase();
  return candidates.some((c) => t === c.toLowerCase() || t.endsWith(c.toLowerCase()));
}

const emailTypHints = [
  "email",
  "emails",
  "preferred_username",
  "upn",
  "emailaddress",
];

/**
 * Prefer an email-shaped claim value that is not masked; otherwise any non-masked claim that looks like an email.
 */
export function emailFromClientPrincipal(p: ClientPrincipalWithClaims): string {
  const claims = p.claims ?? [];
  const candidates: string[] = [];
  for (const c of claims) {
    const typ = (c.typ ?? "").trim();
    const val = (c.val ?? "").trim();
    if (!val) continue;
    if (looksMasked(val)) continue;
    if (looksLikeEmail(val)) {
      candidates.push(val);
      if (emailTypHints.some((h) => claimTypMatches(typ, [h]))) return val;
    }
  }
  if (candidates.length > 0) return candidates[0];

  const ud = (p.userDetails ?? "").trim();
  if (ud && looksLikeEmail(ud) && !looksMasked(ud)) return ud;
  return "";
}

/**
 * Display name from claims (name, given+surname); avoid masked userDetails.
 */
export function displayNameFromClientPrincipal(p: ClientPrincipalWithClaims): string {
  const claims = p.claims ?? [];

  let given = "";
  let family = "";
  for (const c of claims) {
    const typ = (c.typ ?? "").trim().toLowerCase();
    const val = (c.val ?? "").trim();
    if (!val || looksMasked(val)) continue;
    if (typ === "given_name" || typ.endsWith("givenname")) given = val;
    if (typ === "family_name" || typ.endsWith("surname")) family = val;
  }
  if (given && family) return `${given} ${family}`.trim();

  for (const c of claims) {
    const typ = (c.typ ?? "").trim().toLowerCase();
    const val = (c.val ?? "").trim();
    if (!val || looksMasked(val) || looksLikeEmail(val)) continue;
    if (typ === "name" || typ.endsWith("/name")) return val;
  }

  const ud = (p.userDetails ?? "").trim();
  if (ud && !looksMasked(ud)) return ud;

  return emailFromClientPrincipal(p) || "";
}
